// ============================================================
// MatrixSegmentationsPage.tsx
// Unified Segmentations page — Standard + Customized.
//
// Spec §5.1:
//   Standard  (is_preset=TRUE)  — system-provided, read-only.
//   Customized (is_preset=FALSE) — ALM_ADMIN managed, full CRUD.
//
// Segment builder UX:
//   1. Name the segmentation + choose dimension.
//   2. Click "New segment" → enter segment name → select criteria
//      (which dimension values belong to this segment).
//   Segments are never shown standalone — they only exist inside a segmentation.
//
// Route:  /parameters/segmentations
// API:    GET/POST/PUT/DELETE /api/v1/alm/matrix-segmentations
// ============================================================

import { useState, useCallback, useMemo } from 'react';
import {
  Plus,
  Lock,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Tag,
  TableProperties,
  Check,
} from 'lucide-react';

import {
  useSegmentations,
  useCreateSegmentation,
  useUpdateSegmentation,
  useDeleteSegmentation,
  type MatrixSegmentation,
  type MatrixDimension,
  type MatrixSegmentationCreate,
  type MatrixSegmentationMapping,
} from './segmentationApi';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// ── Dimension vocabulary ──────────────────────────────────────────────────────

const DIMENSION_VALUES: Record<MatrixDimension, { value: string; label: string }[]> = {
  INSTRUMENT_TYPE: [
    { value: 'BOND',       label: 'BOND — Obligations souveraines' },
    { value: 'T_BILL',     label: 'T_BILL — Bons du Trésor' },
    { value: 'CD',         label: 'CD — Certificats de dépôt' },
    { value: 'LOAN',       label: 'LOAN — Crédits clientèle' },
    { value: 'OVERDRAFT',  label: 'OVERDRAFT — Découverts' },
    { value: 'INTERBANK',  label: 'INTERBANK — Interbancaire' },
    { value: 'NOSTRO',     label: 'NOSTRO — Comptes nostro' },
    { value: 'DAV',        label: 'DAV — Dépôts à vue' },
    { value: 'SAVINGS',    label: 'SAVINGS — Épargne' },
    { value: 'TERM',       label: 'TERM — Dépôts à terme' },
    { value: 'IRS',        label: 'IRS — Swaps de taux' },
    { value: 'CCS',        label: 'CCS — Cross-currency swaps' },
    { value: 'FX_FORWARD', label: 'FX_FORWARD — Forwards de change' },
    { value: 'FX_SWAP',    label: 'FX_SWAP — Swaps cambistes' },
    { value: 'COMMITMENT', label: 'COMMITMENT — Hors bilan' },
  ],
  BS_CLASS: [
    { value: 'ASSET',         label: 'ASSET — Actifs' },
    { value: 'LIABILITY',     label: 'LIABILITY — Passifs' },
    { value: 'OBS_ASSET',     label: 'OBS_ASSET — Engagements donnés' },
    { value: 'OBS_LIABILITY', label: 'OBS_LIABILITY — Engagements reçus' },
  ],
  CURRENCY: [
    { value: 'MAD', label: 'MAD — Dirham marocain' },
    { value: 'EUR', label: 'EUR — Euro' },
    { value: 'USD', label: 'USD — Dollar US' },
    { value: 'GBP', label: 'GBP — Livre sterling' },
    { value: 'XOF', label: 'XOF — Franc CFA' },
    { value: 'CHF', label: 'CHF — Franc suisse' },
    { value: 'JPY', label: 'JPY — Yen japonais' },
  ],
  SENS: [
    { value: 'INFLOW',  label: 'INFLOW — Encaissements' },
    { value: 'OUTFLOW', label: 'OUTFLOW — Décaissements' },
  ],
  BS_POSITION: [
    { value: 'BILAN',      label: 'BILAN — Bilan (actifs + passifs)' },
    { value: 'HORS_BILAN', label: 'HORS_BILAN — Hors-bilan (engagements)' },
  ],
  PCEC: [
    { value: 'TRESORERIE', label: 'TRESORERIE — Trésorerie et interbancaire' },
    { value: 'CREDITS',    label: 'CREDITS — Crédits à la clientèle' },
    { value: 'TITRES',     label: 'TITRES — Titres et valeurs mobilières' },
    { value: 'DEPOTS',     label: 'DEPOTS — Dépôts clientèle' },
    { value: 'DERIVES',    label: 'DERIVES — Dérivés et change' },
    { value: 'HORS_BILAN', label: 'HORS_BILAN — Engagements hors bilan' },
    { value: 'AUTRES',     label: 'AUTRES — Autres opérations' },
  ],
};

const DIMENSION_LABELS: Record<MatrixDimension, string> = {
  INSTRUMENT_TYPE: 'Instrument type',
  BS_CLASS:        'BS class',
  CURRENCY:        'Currency',
  SENS:            'Sens',
  BS_POSITION:     'Bilan / Hors-bilan',
  PCEC:            'Chap. PCEC',
};

const DIMENSION_BADGE: Record<MatrixDimension, string> = {
  INSTRUMENT_TYPE: 'bg-blue-100 text-blue-800 border-blue-200',
  BS_CLASS:        'bg-purple-100 text-purple-800 border-purple-200',
  CURRENCY:        'bg-emerald-100 text-emerald-800 border-emerald-200',
  SENS:            'bg-amber-100 text-amber-800 border-amber-200',
  BS_POSITION:     'bg-rose-100 text-rose-800 border-rose-200',
  PCEC:            'bg-cyan-100 text-cyan-800 border-cyan-200',
};

const SEGMENT_COLOURS = [
  'bg-blue-50 text-blue-700 border-blue-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-rose-50 text-rose-700 border-rose-200',
  'bg-violet-50 text-violet-700 border-violet-200',
  'bg-teal-50 text-teal-700 border-teal-200',
  'bg-orange-50 text-orange-700 border-orange-200',
  'bg-cyan-50 text-cyan-700 border-cyan-200',
  'bg-lime-50 text-lime-700 border-lime-200',
];

// ── Types ─────────────────────────────────────────────────────────────────────

/** One segment inside the editor: a name + set of assigned dimension values. */
interface SegmentDraft {
  id: string;            // local draft ID (not the DB mapping id)
  label: string;         // e.g. "Crédits clientèle"
  dimValues: string[];   // e.g. ['LOAN', 'OVERDRAFT']
}

interface FormState {
  name:        string;
  description: string;
  dimension:   MatrixDimension;
  segments:    SegmentDraft[];
}

function uid() { return Math.random().toString(36).slice(2); }

function blankForm(): FormState {
  return { name: '', description: '', dimension: 'INSTRUMENT_TYPE', segments: [] };
}

function formFromSegmentation(seg: MatrixSegmentation): FormState {
  // Group mappings by segmentLabel → one SegmentDraft per label
  const byLabel: Record<string, string[]> = {};
  for (const m of seg.mappings) {
    if (!byLabel[m.segmentLabel]) byLabel[m.segmentLabel] = [];
    byLabel[m.segmentLabel].push(m.dimensionValue);
  }
  const segments: SegmentDraft[] = Object.entries(byLabel).map(([label, dimValues]) => ({
    id: uid(), label, dimValues,
  }));
  return { name: seg.name, description: seg.description ?? '', dimension: seg.dimension, segments };
}

function formToPayload(form: FormState): MatrixSegmentationCreate {
  const mappings: MatrixSegmentationMapping[] = [];
  form.segments.forEach((seg, segIdx) => {
    seg.dimValues.forEach((dv, dvIdx) => {
      mappings.push({ dimensionValue: dv, segmentLabel: seg.label, sortOrder: segIdx * 100 + dvIdx });
    });
  });
  return {
    name:        form.name.trim(),
    description: form.description.trim() || undefined,
    dimension:   form.dimension,
    sortOrder:   100,
    mappings,
  };
}

// ── NewSegmentForm — inline form to add one segment ───────────────────────────

interface NewSegmentFormProps {
  dimension:     MatrixDimension;
  usedDimValues: Set<string>;
  editingDraft?: SegmentDraft;          // set when editing an existing draft
  onAdd:   (draft: SegmentDraft) => void;
  onCancel: () => void;
}

function NewSegmentForm({ dimension, usedDimValues, editingDraft, onAdd, onCancel }: NewSegmentFormProps) {
  const [name, setName]       = useState(editingDraft?.label ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(editingDraft?.dimValues ?? []),
  );

  const dimValues = DIMENSION_VALUES[dimension];

  function toggle(v: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  }

  function handleAdd() {
    const label = name.trim();
    if (!label || selected.size === 0) return;
    onAdd({ id: editingDraft?.id ?? uid(), label, dimValues: [...selected] });
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-3">
      {/* Segment name */}
      <div className="flex gap-2 items-end">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Segment name</Label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
            placeholder="e.g. Crédits clientèle"
            className="h-7 text-xs"
            autoFocus
          />
        </div>
        <Button size="sm" className="h-7 text-xs px-3" onClick={handleAdd}
          disabled={!name.trim() || selected.size === 0}>
          <Check className="h-3 w-3 mr-1" />
          {editingDraft ? 'Update' : 'Add segment'}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onCancel}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Criteria — checkboxes for each dimension value */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-600/80 mb-1.5">
          Criteria — select {DIMENSION_LABELS[dimension]} values
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {dimValues.map(dv => {
            const checked   = selected.has(dv.value);
            const occupied  = !checked && usedDimValues.has(dv.value);
            return (
              <label
                key={dv.value}
                className={cn(
                  'flex items-center gap-1.5 cursor-pointer select-none rounded px-1.5 py-0.5 text-xs',
                  occupied
                    ? 'opacity-40 cursor-not-allowed'
                    : 'hover:bg-blue-100/60',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={occupied}
                  onChange={() => !occupied && toggle(dv.value)}
                  className="h-3 w-3 accent-blue-600"
                />
                <span className="font-mono text-[11px]">{dv.value}</span>
                <span className="text-gray-500 text-[10px] truncate">{dv.label.split(' — ')[1]}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── SegmentationEditor ────────────────────────────────────────────────────────

interface EditorProps {
  initial:   FormState;
  editingId: string | null;
  onSave:    (form: FormState) => void;
  onCancel:  () => void;
  isSaving:  boolean;
  saveError: string | null;
}

function SegmentationEditor({ initial, editingId, onSave, onCancel, isSaving, saveError }: EditorProps) {
  const [form, setForm]           = useState<FormState>(initial);
  const [addingSegment, setAddingSegment] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);

  // Set of dimension values already assigned to any segment
  const usedDimValues = useMemo(() => {
    const used = new Set<string>();
    form.segments.forEach(s => s.dimValues.forEach(v => used.add(v)));
    return used;
  }, [form.segments]);

  // When editing a draft, exclude its own values from "occupied"
  const usedExcludingEdit = useMemo(() => {
    if (!editingDraftId) return usedDimValues;
    const editing = form.segments.find(s => s.id === editingDraftId);
    const used = new Set(usedDimValues);
    editing?.dimValues.forEach(v => used.delete(v));
    return used;
  }, [usedDimValues, editingDraftId, form.segments]);

  function handleDimensionChange(dim: MatrixDimension) {
    setForm(f => ({ ...f, dimension: dim, segments: [] }));
    setAddingSegment(false);
    setEditingDraftId(null);
  }

  function addDraft(draft: SegmentDraft) {
    if (editingDraftId) {
      setForm(f => ({ ...f, segments: f.segments.map(s => s.id === draft.id ? draft : s) }));
      setEditingDraftId(null);
    } else {
      setForm(f => ({ ...f, segments: [...f.segments, draft] }));
      setAddingSegment(false);
    }
  }

  function removeDraft(id: string) {
    setForm(f => ({ ...f, segments: f.segments.filter(s => s.id !== id) }));
    if (editingDraftId === id) setEditingDraftId(null);
  }

  const canSave = form.name.trim().length > 0 && !isSaving;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {editingId ? 'Edit segmentation' : 'New segmentation'}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Name the segmentation, choose a dimension, then add segments with their criteria.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel} className="h-7 w-7">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Identity */}
      <Card className="shadow-none border-gray-200">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium">Segmentation</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Name <span className="text-red-500">*</span></Label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. myBank_MNI_SEG"
              className="text-sm h-8"
              disabled={isSaving}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Input
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional"
              className="text-sm h-8"
              disabled={isSaving}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Dimension <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-3 gap-1.5">
              {(
                ['INSTRUMENT_TYPE', 'BS_CLASS', 'CURRENCY', 'SENS', 'BS_POSITION', 'PCEC'] as MatrixDimension[]
              ).map(dim => (
                <button
                  key={dim}
                  type="button"
                  disabled={!!editingId || isSaving}
                  onClick={() => handleDimensionChange(dim)}
                  className={cn(
                    'rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors text-center',
                    form.dimension === dim
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                    editingId && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {DIMENSION_LABELS[dim]}
                </button>
              ))}
            </div>
            {editingId && (
              <p className="text-[10px] text-gray-400">Dimension cannot be changed after creation.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Segments */}
      <Card className="shadow-none border-gray-200">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">Segments</CardTitle>
            {!addingSegment && !editingDraftId && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setAddingSegment(true)}
                disabled={isSaving}
              >
                <Plus className="h-3 w-3 mr-1" />
                New segment
              </Button>
            )}
          </div>
          <CardDescription className="text-xs">
            Each segment groups {DIMENSION_LABELS[form.dimension]} values under a single label in the gap matrix.
            Values not assigned to any segment fall back to their raw code.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2">

          {/* Existing segment cards */}
          {form.segments.map((seg, idx) => {
            const colour = SEGMENT_COLOURS[idx % SEGMENT_COLOURS.length];
            const isEditing = editingDraftId === seg.id;
            if (isEditing) {
              return (
                <NewSegmentForm
                  key={seg.id}
                  dimension={form.dimension}
                  usedDimValues={usedExcludingEdit}
                  editingDraft={seg}
                  onAdd={addDraft}
                  onCancel={() => setEditingDraftId(null)}
                />
              );
            }
            return (
              <div
                key={seg.id}
                className={cn('rounded-lg border p-2.5 space-y-1.5', colour)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3 w-3 shrink-0" />
                    <span className="text-xs font-semibold">{seg.label}</span>
                    <span className="text-[10px] opacity-60">
                      ({seg.dimValues.length} value{seg.dimValues.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => { setAddingSegment(false); setEditingDraftId(seg.id); }}
                      disabled={isSaving}
                      className="rounded p-0.5 hover:bg-white/60 transition-colors"
                      title="Edit segment"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeDraft(seg.id)}
                      disabled={isSaving}
                      className="rounded p-0.5 hover:bg-white/60 transition-colors"
                      title="Remove segment"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                {/* Dimension value chips */}
                <div className="flex flex-wrap gap-1">
                  {seg.dimValues.map(dv => (
                    <span
                      key={dv}
                      className="rounded border border-current/20 bg-white/50 px-1.5 py-0 text-[10px] font-mono"
                    >
                      {dv}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}

          {/* New segment inline form */}
          {addingSegment && (
            <NewSegmentForm
              dimension={form.dimension}
              usedDimValues={usedDimValues}
              onAdd={addDraft}
              onCancel={() => setAddingSegment(false)}
            />
          )}

          {/* Empty state */}
          {form.segments.length === 0 && !addingSegment && (
            <div className="rounded-md border border-dashed border-gray-200 py-8 text-center">
              <TableProperties className="mx-auto h-5 w-5 text-gray-300 mb-1.5" />
              <p className="text-xs text-gray-400">
                Click <strong>New segment</strong> to add your first segment.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {saveError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {saveError}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>Cancel</Button>
        <Button size="sm" onClick={() => onSave(form)} disabled={!canSave}>
          {isSaving ? 'Saving…' : editingId ? 'Update segmentation' : 'Create segmentation'}
        </Button>
      </div>
    </div>
  );
}

// ── SegmentationRow ───────────────────────────────────────────────────────────

interface RowProps {
  seg:        MatrixSegmentation;
  onEdit:     () => void;
  onDelete:   () => void;
  isDeleting: boolean;
}

function SegmentationRow({ seg, onEdit, onDelete, isDeleting }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Group mappings by segmentLabel
  const grouped = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const m of seg.mappings) {
      if (!map[m.segmentLabel]) map[m.segmentLabel] = [];
      map[m.segmentLabel].push(m.dimensionValue);
    }
    return map;
  }, [seg.mappings]);

  const segLabels = Object.keys(grouped);

  return (
    <div className={cn('border border-gray-200 rounded-lg bg-white', isDeleting && 'opacity-50')}>
      {/* Row header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="text-gray-400 hover:text-gray-600 shrink-0"
          disabled={seg.mappings.length === 0}
        >
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate">{seg.name}</span>
            <span className={cn(
              'inline-flex items-center rounded border px-1.5 py-0 text-[10px] font-medium',
              DIMENSION_BADGE[seg.dimension],
            )}>
              {seg.dimension}
            </span>
            {seg.isPreset && (
              <span className="inline-flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0 text-[10px] text-gray-500">
                <Lock className="h-2.5 w-2.5" /> Standard
              </span>
            )}
          </div>
          {seg.description && (
            <p className="text-[11px] text-gray-400 mt-0.5 truncate">{seg.description}</p>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-gray-400">
            {segLabels.length} segment{segLabels.length !== 1 ? 's' : ''}
            {segLabels.length > 0 && ` · ${seg.mappings.length} criteria`}
          </span>

          {!seg.isPreset && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-gray-700"
                onClick={onEdit} title="Edit">
                <Pencil className="h-3 w-3" />
              </Button>
              {confirmDelete ? (
                <span className="flex items-center gap-1">
                  <span className="text-[10px] text-red-600">Delete?</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600 hover:text-red-800"
                    onClick={onDelete}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400"
                    onClick={() => setConfirmDelete(false)}>
                    <X className="h-3 w-3" />
                  </Button>
                </span>
              ) : (
                <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-600"
                  onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded: segments with their criteria */}
      {expanded && segLabels.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 rounded-b-lg">
          <div className="flex flex-wrap gap-4">
            {segLabels.map((label, idx) => (
              <div key={label}>
                <span className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium mb-1',
                  SEGMENT_COLOURS[idx % SEGMENT_COLOURS.length],
                )}>
                  <Tag className="h-2.5 w-2.5" />
                  {label}
                </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {grouped[label].map(dv => (
                    <span key={dv}
                      className="rounded bg-white border border-gray-200 px-1.5 py-0 text-[10px] font-mono text-gray-600">
                      {dv}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── MatrixSegmentationsPage ───────────────────────────────────────────────────

type PanelState = { mode: 'idle' } | { mode: 'create' } | { mode: 'edit'; seg: MatrixSegmentation };

export function MatrixSegmentationsPage() {
  const { data: segs = [], isLoading, error } = useSegmentations();
  const createMut = useCreateSegmentation();
  const updateMut = useUpdateSegmentation();
  const deleteMut = useDeleteSegmentation();

  const [panel, setPanel]         = useState<PanelState>({ mode: 'idle' });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const standardSegs   = segs.filter(s =>  s.isPreset);
  const customizedSegs = segs.filter(s => !s.isPreset);

  const handleSave = useCallback(async (form: FormState) => {
    const payload = formToPayload(form);
    try {
      if (panel.mode === 'edit') {
        await updateMut.mutateAsync({ id: panel.seg.id, ...payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      setPanel({ mode: 'idle' });
    } catch { /* error shown via saveError prop */ }
  }, [panel, createMut, updateMut]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try { await deleteMut.mutateAsync(id); }
    finally { setDeletingId(null); }
  }, [deleteMut]);

  const isSaving  = createMut.isPending || updateMut.isPending;
  const saveError = createMut.error?.message ?? updateMut.error?.message ?? null;
  const editorInitial: FormState =
    panel.mode === 'edit' ? formFromSegmentation(panel.seg) : blankForm();

  return (
    <div className="flex h-full gap-0 overflow-hidden">

      {/* ── Left pane — list ───────────────────────────────────────── */}
      <div className={cn(
        'flex flex-col overflow-y-auto transition-all duration-200',
        panel.mode !== 'idle' ? 'w-[46%] border-r border-gray-200' : 'w-full',
      )}>
        {/* Page header */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-6 py-4 border-b border-gray-100">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Segmentations</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Named groupings of dimension values for the gap matrix.
              Standard segmentations are read-only; Customized are fully editable.
            </p>
          </div>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={() => setPanel({ mode: 'create' })}
            disabled={panel.mode === 'create'}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New segmentation
          </Button>
        </div>

        <div className="flex-1 px-6 py-5 space-y-8">
          {isLoading && (
            <p className="text-sm text-gray-400 text-center py-12">Loading…</p>
          )}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-4 py-3">
              Failed to load segmentations: {(error as Error).message}
            </p>
          )}

          {/* Standard */}
          {standardSegs.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Standard segmentations
                </h2>
                <span className="text-[10px] text-gray-300">— system-provided, read-only</span>
              </div>
              <div className="space-y-2">
                {standardSegs.map(seg => (
                  <SegmentationRow key={seg.id} seg={seg}
                    onEdit={() => {}} onDelete={() => {}} isDeleting={false} />
                ))}
              </div>
            </section>
          )}

          {/* Customized */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Customized segmentations
              </h2>
              <span className="text-[10px] text-gray-300">— ALM_ADMIN managed</span>
            </div>

            {customizedSegs.length === 0 && !isLoading && (
              <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center">
                <TableProperties className="mx-auto h-7 w-7 text-gray-300 mb-2" />
                <p className="text-sm font-medium text-gray-500">No customized segmentations yet</p>
                <p className="text-xs text-gray-400 mt-1">Click "New segmentation" to create your first one.</p>
              </div>
            )}
            <div className="space-y-2">
              {customizedSegs.map(seg => (
                <SegmentationRow key={seg.id} seg={seg}
                  onEdit={() => setPanel({ mode: 'edit', seg })}
                  onDelete={() => handleDelete(seg.id)}
                  isDeleting={deletingId === seg.id} />
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* ── Right pane — editor ────────────────────────────────────── */}
      {panel.mode !== 'idle' && (
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-gray-50/40">
          <SegmentationEditor
            key={panel.mode === 'edit' ? panel.seg.id : 'new'}
            initial={editorInitial}
            editingId={panel.mode === 'edit' ? panel.seg.id : null}
            onSave={handleSave}
            onCancel={() => setPanel({ mode: 'idle' })}
            isSaving={isSaving}
            saveError={saveError}
          />
        </div>
      )}
    </div>
  );
}
